import apiClient from './apiClient'
import { _OperationDetailsRequest } from '../features/cafm/operationDetailsSlice'
import { SecretUpdatePayload } from '../features/cafm/secretValueSlice'
import { NewAddedValueMapping } from '../components/EditFieldMapping/ValueMapping'
import { updateAPIBaseURLtype } from '../components/Common/OrganizationDetailsComponent'
import { omit } from 'lodash'
import {
  ExecuteTestData,
  ColumnConfigPayload,
  ColumnExportConfigRow,
  ColumnExportConfigUpdate,
} from '../utils/interfaces'

/**
 * cafmApi.ts
 * ---------------------
 * Centralized API layer for CAFM (Computer Aided Facility Management) integrations.
 *
 * Responsibilities:
 * - Encapsulates all HTTP calls related to CAFM mappings, endpoints, secrets,
 *   value mappings, release/export/import flows, and onboarding test execution.
 * - Acts as the single source of truth for CAFM backend contract usage.
 * - Performs payload normalization and sanitization before API calls
 *   (e.g., removing backend-managed IDs).
 *
 * Design notes:
 * - Uses `apiClient` for authenticated backend calls.
 * - Uses native `fetch` only for dummy/local APIs where backend is not available.
 * - Keeps business logic minimal; complex transformations should live in services/hooks.
 *
 *  Do NOT:
 * - Add UI-specific logic here.
 * - Mutate incoming payload objects directly.
 * - Send backend-generated IDs (e.g., value_mapping_id) unless explicitly required.
 */

// MIME type constants
const MIME_TYPES = {
  APPLICATION_JSON: 'application/json',
} as const

interface ValueMapping {
  value_mapping_id?: string
  source_field_name: string
  converted_value?: string
  source_value?: string
  destination_field_name?: string | null
}

type FieldMapping = {
  field_name: string
  is_mandatory: boolean
  data_type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  request_location: string
  default_value: string | number | boolean | null | unknown[]
  source_object_field_name?: string
  new_field_id?: string
  merge_lineitems?: boolean
  match_values?: {
    match_value_type: string
    values_mapping: ValueMapping[]
  }
}

type UpdateFieldMapping = {
  field_id: string
  field_name: string
  is_mandatory: boolean
  data_type: null | 'body' | 'query' | 'params'
  request_location: string
  default_value: string | number | boolean | null | unknown[]
  source_object_field_name?: string
  merge_lineitems?: boolean
}

export type RequestPayload = {
  operation_id: string
  api_endpoint_url?: string
  api_method?: string
  api_header_content_type?: string
  decider_fields_name?: string
  decider_fields_value?: string[]
  added_new_field_mapping?: FieldMapping[]
  update_field_mapping?: UpdateFieldMapping[]
  delete_field_mapping?: string[]
  needs_delay?: boolean
  delay_mins?: number
  needs_escalation?: boolean
  escalation_email?: string | null
  enable_email?: boolean
  email_address?: string | null
  configure_status?: boolean
  file_enabled_statuses?: string[]
}

// Create a new CAFM mapping
export const createCafmMapping = async (data: any) => {
  const response = await apiClient.post('/cafm', data)
  return response
}

interface FetchCafmDetailsParams {
  start: number
  limit: number
  org_id?: string // Optional for admin override, otherwise uses current org
  isPersonalOrg?: boolean // Flag for dummy Personal org (id=0)
  external_id?: string // External ID for Personal orgs
}

export const fetchCafmDetails = async ({
  start,
  limit,
  org_id,
  isPersonalOrg,
  external_id,
}: FetchCafmDetailsParams) => {
  const payload: any = { start, limit }

  if (isPersonalOrg) {
    // For Personal orgs, send external_id instead of org_id
    if (external_id) {
      payload.external_id = external_id
    } else {
      // Fallback: try sessionStorage
      const orgs = JSON.parse(sessionStorage.getItem('organizations') || '[]')
      const personalOrg = orgs.find((org: any) => org.type?.toLowerCase() === 'personal')
      if (personalOrg?.externalId) {
        payload.external_id = personalOrg.externalId
      } else {
        throw new Error('Personal organization external_id not found - please provide external_id parameter')
      }
    }
  } else if (org_id) {
    payload.org_id = org_id
  }

  const response = await apiClient.post('/cafm/details', payload)
  return response
}

// Fetch a single CAFM mapping by ID
export const fetchCafmById = async (id: any) => {
  const response = await apiClient.get(`/cafm/${id}`)
  return response
}

// Fetch a single CAFM mapping by ID
const source_systems = ['fsm', 'Konect', 'Konect ENA', 'FSM CDM']

export const fetchMasterData = async (source_system: string, country?: string) => {
  // Normalize incoming value
  const normalized = source_system.toLowerCase()
  // Try to find the correct one from the array
  const matchedSource = source_systems.find((sys) => sys.toLowerCase() === normalized)
  if (!matchedSource) {
    console.warn(
      `cafmApi - Invalid source_system "${source_system}" passed. Allowed values: ${source_systems.join(', ')}`,
    )
    throw new Error(`Invalid source_system: ${source_system}`)
  }

  const response = await apiClient.post(`/cafm/master-data`, { source_system: matchedSource, ...(country ? { country } : {}) })
  return response
}

// Fetch master data admin (flat listing of all 3 tables for a source system)
export const fetchMasterDataAdmin = async (
  source_system: string,
  decider_search?: string,
  event_field_search?: string,
) => {
  const response = await apiClient.post('/cafm/master-data-admin', {
    source_system,
    ...(decider_search ? { decider_search } : {}),
    ...(event_field_search ? { event_field_search } : {}),
  })
  return response
}

// Discriminated union covering all manage-master-data actions
export type ManageMasterDataPayload =
  | {
      action: 'addDecider'
      decider_fields_name: string
      decider_fields_value: string
      object_type: string
      source_system: string
      status_label?: string
      decider_fields_label?: string
    }
  | {
      action: 'updateDecider'
      decider_id: number
      decider_fields_name: string
      decider_fields_value: string
      object_type: string
      source_system: string
      status_label?: string
      decider_fields_label?: string
    }
  | { action: 'deleteDecider'; decider_id: number }
  | { action: 'addEventField'; event_field_name: string; event_field_label: string; object_type: string }
  | {
      action: 'updateEventField'
      event_field_id: number
      event_field_name: string
      event_field_label: string
      object_type: string
    }
  | { action: 'deleteEventField'; event_field_id: number }
  | { action: 'removeDeciderRelation'; decider_id: number; event_field_id: number }
  | { action: 'bulkAddDeciderRelations'; decider_id: number; event_field_ids: number[] }

// Manage master data (CRUD operations)
export const manageMasterData = async (payload: ManageMasterDataPayload) => {
  const response = await apiClient.post('/cafm/manage-master-data', payload)
  return response
}

// Column config - fetch schema, active columns per source system, or full export
export const fetchColumnConfig = async (payload: ColumnConfigPayload) => {
  const response = await apiClient.post('/cafm/column-config', payload)
  return response
}

export const saveColumnExportConfig = async (updates: ColumnExportConfigUpdate[]) => {
  const response = await apiClient.post('/cafm/column-config', { action: 'save_export_config', updates })
  return response
}

export const initializeColumnDefaults = async () => {
  const response = await apiClient.post('/cafm/column-config', { action: 'seed_from_schema' })
  return response
}

// delete single CAFM mapping details
export const deleteMapping = async (payload: { integration_id: string; org_id: string }) => {
  const response = await apiClient.delete('/cafm/cafm-endpoint', { data: payload })
  return response
}

// Get integration details - supports normal, multi, and encrypted modes
export const getIntegrationDetails = async (payload: { integration_id: number | number[]; release?: boolean }) => {
  const response = await apiClient.post('/cafm/get-integration-details', payload)
  return response
}

// Decrypt integration data using Web Crypto API
export const decryptIntegrationData = async (encryptedFile: {
  data: string
  encryption: { iv: string }
  metadata: { kone_customer: string; kone_system: string }
}) => {
  try {
    const { data: encryptedData, encryption, metadata } = encryptedFile
    const { iv } = encryption
    const { kone_customer: customerName, kone_system: sourceSystem } = metadata

    // Create the same deterministic key used for encryption
    const keyString = `${customerName}_${sourceSystem}_kone_integration`
    const keyBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(keyString))

    // Import the key for decryption
    const cryptoKey = await crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-CBC' }, false, ['decrypt'])

    // Convert hex strings to ArrayBuffers
    const encryptedBuffer = new Uint8Array(encryptedData.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [])
    const ivBuffer = new Uint8Array(iv.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [])

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: ivBuffer }, cryptoKey, encryptedBuffer)

    // Convert back to JSON
    const decryptedText = new TextDecoder().decode(decryptedBuffer)
    return JSON.parse(decryptedText)
  } catch (error) {
    console.error('Decryption failed:', error)
    throw new Error('Failed to decrypt integration data: ' + (error as Error).message)
  }
}

// Fetch CAFM details with pagination
export const workorderApi = async ({ start, limit }: { start: number; limit: number }) => {
  const response = await apiClient.post('/cafm/workorder', { start, limit })
  return response
}

// cafmAPI.ts -- dummy api
export const updateApiBaseUrl = async (apiUrl: string) => {
  try {
    const response = await fetch('/api/organization-details', {
      method: 'POST',
      headers: {
        'Content-Type': MIME_TYPES.APPLICATION_JSON,
      },
      body: JSON.stringify({ apiUrl }),
    })
    if (!response.ok) {
      throw new Error('Failed to update API URL')
    }
    return await response.json()
  } catch (error) {
    console.error('Error updating API Base URL:', error)
    throw error
  }
}

export const getFieldsMappingDetailsViaOperationId = async (operation_id: string) => {
  const response = await apiClient.post('/cafm/field-mapping/details', { operation_id })
  return response
}

export const getFieldsMappingDetailsViaEscalationId = async (escalation_id: string, integration_id?: string) => {
  const response = await apiClient.post('/cafm/field-mapping/details', {
    escalation_id: Number(escalation_id),
    ...(integration_id ? { integration_id: Number(integration_id) } : {}),
  })
  return response
}

/**
 * Utility function to remove value_mapping_id from field mapping payload
 * This ensures the field mapping API doesn't receive value_mapping_id
 */
const removeValueMappingIds = (payload: RequestPayload): RequestPayload => {
  const cleanPayload = { ...payload }

  // Clean added_new_field_mapping
  if (cleanPayload.added_new_field_mapping) {
    cleanPayload.added_new_field_mapping = cleanPayload.added_new_field_mapping.map((field) => {
      const cleanField = { ...field }

      // Remove value_mapping_id from match_values.values_mapping if it exists
      if (cleanField.match_values?.values_mapping) {
        cleanField.match_values = {
          ...cleanField.match_values,
          values_mapping: cleanField.match_values.values_mapping.map((mapping) => omit(mapping, 'value_mapping_id')),
        }
      }

      return cleanField
    })
  }

  // Clean update_field_mapping (if it has similar structure in the future)
  if (cleanPayload.update_field_mapping) {
    cleanPayload.update_field_mapping = cleanPayload.update_field_mapping.map((field) => {
      const cleanField = { ...field } as any

      // Remove value_mapping_id from match_values.values_mapping if it exists
      if (cleanField.match_values?.values_mapping) {
        cleanField.match_values = {
          ...cleanField.match_values,
          values_mapping: cleanField.match_values.values_mapping.map((mapping: any) =>
            omit(mapping, 'value_mapping_id'),
          ),
        }
      }

      return cleanField
    })
  }

  return cleanPayload
}

export const updateFieldMappingDetails = async (request: RequestPayload) => {
  // Remove value_mapping_id from the payload before sending to field mapping API
  const cleanedRequest = removeValueMappingIds(request)
  const response = await apiClient.patch('/cafm/field-mapping', cleanedRequest)
  return response
}

// store CAFM endpoints
export const storeCafmEndpoint = async (payload: object) => {
  const response = await apiClient.post(`/cafm/endpoint`, payload)
  return response
}

//get CAFM endpoints
export const getCafmEndpoints = async (integrationId: string, start: number, limit: number, koneSystem?: string) => {
  const response = await apiClient.post(`/cafm/endpoints`, {
    int_id: integrationId,
    start,
    limit,
    kone_system: koneSystem,
  })
  return response
}

export const getOperationDetails = async (payload: _OperationDetailsRequest) => {
  const response = await apiClient.post(`cafm/mappings`, payload)
  return response
}

export const getSecretValue = async (integation_id: string) => {
  const response = await apiClient.get(`cafm/secret/${integation_id}`)
  return response
}

export const updateSecretValue = async (payload: SecretUpdatePayload) => {
  const response = await apiClient.patch(`cafm/secret`, payload)
  return response
}

export const updateValueMapping = async (payload: NewAddedValueMapping) => {
  const response = await apiClient.patch(`cafm/value-mapping`, payload)
  return response
}

// Save file rename configuration
export const saveFileRenameDetails = async (payload: {
  operation_id: string
  staticValue: string
  selectedFields: string[]
  preview: string
  field_mapping_id?: string
  value_mapping_id?: string | number | null
}) => {
  const response = await apiClient.post('cafm/file-rename-details', payload)
  return response
}

export const updateAPIBaseURL = async (payload: updateAPIBaseURLtype) => {
  const response = await apiClient.patch(`cafm/api-base-url`, payload)
  return response
}

// delete single CAFM mapping endpoint
export const deleteEndpoint = async (payload: {
  operation_id?: number
  escalation_id?: number
  integration_id?: string
}) => {
  const response = await apiClient.delete('/cafm/endpoint-details', { data: payload })
  return response
}

// update operation priority for operations (Konect ENA feature)
export const updateExecutionSequence = async (payload: {
  sequences: Array<{ operation_id: number; operation_priority: number }>
}) => {
  const response = await apiClient.patch('/cafm/execution-sequence', payload)
  return response
}

// Approve (publish) an AI-created endpoint
export const approveEndpoint = async (payload: { operation_id: string }) => {
  const response = await apiClient.post('/cafm/approve-endpoint', payload)
  return response
}

// Dummy API: Check if current org has existing mapping
export const checkOrgHasMapping = async (orgId: string) => {
  // TODO: Replace with real API call
  // Simulate: returns { hasMapping: boolean }
  console.log('Checking mapping for org:', orgId)
  return Promise.resolve({ hasMapping: false })
}

// Dummy API: Get list of organizations to replicate from
export const fetchReplicableOrganizations = async () => {
  // TODO: Replace with real API call
  // Simulate: returns [{ id: string, name: string }]
  return Promise.resolve([
    { id: 'org1', name: 'Organization One' },
    { id: 'org2', name: 'Organization Two' },
    { id: 'org3', name: 'Organization Three' },
  ])
}

// API to replicate integration configuration
export const copyIntegration = async (integration_id: string, target_org: string) => {
  const response = await apiClient.post('/cafm/copy-integration', {
    integration_id: Number(integration_id),
    target_org: Number(target_org),
  })
  return response
}

export interface SimilarIntegration {
  integration_id: number
  integration_name: string
  integration_partener: string
  cust_id: string
  kone_system: string
  integration_type: string
  org_id: string
  updated_at: string
  operation_count: string
}

export interface IntegrationTemplateDeciderField {
  field_name: string
  field_value: string
}

export interface IntegrationTemplateOperationPreview {
  operation_id: string
  event_name: string
  operation_type: string
  api_method?: string
  api_endpoint_url?: string
  field_mapping_count: number
  // Decider(s) that select this operation (e.g. status = OPEN).
  decider_fields?: IntegrationTemplateDeciderField[]
}

// ES-13778: find existing integrations that match the same platform/integration type/FL system,
// to suggest as a template for a mapping currently being created on the onboarding tool.
export const searchSimilarIntegrations = async (params: {
  kone_system: string
  integration_type: string
  facility_management_system_name: string
  exclude_org_id?: string | null
}): Promise<{ data: SimilarIntegration[] }> => {
  // NOTE: apiClient's response interceptor already unwraps to `response.data`,
  // so this resolves to the backend body `{ data: SimilarIntegration[] }`.
  const response = await apiClient.post('/cafm/search-similar-integrations', params)
  return response as unknown as { data: SimilarIntegration[] }
}

// Read-only preview of a candidate template's operations (no secrets/auth info returned).
export const previewIntegrationTemplate = async (
  integration_id: number,
): Promise<{ data: IntegrationTemplateOperationPreview[] }> => {
  // apiClient interceptor already unwraps to `response.data` (the backend body).
  const response = await apiClient.post('/cafm/preview-integration-template', { integration_id })
  return response as unknown as { data: IntegrationTemplateOperationPreview[] }
}

// Copies operations/field/value mappings from the selected template integration into the
// newly created integration.
export const applyIntegrationTemplate = async (source_integration_id: number, target_integration_id: number) => {
  const response = await apiClient.post('/cafm/apply-integration-template', {
    source_integration_id,
    target_integration_id,
  })
  return response
}

// Release Config API interface
interface ReleaseConfigData {
  release: boolean
  integration_id: string
  customer_name: string
  base_url: string
  auth_type: 'basic' | 'custom' | 'header' | 'apiKey' | 'customHeader'
  // Basic auth fields
  auth_url?: string
  username_body?: string
  password_body?: string
  // Custom auth fields
  username_auth?: string
  password_auth?: string
  content_type?: string
  token_delivery?: 'bearer' | 'x-api-key'
  response_key_path?: string
  expiry_key_path?: string
  token_prefix?: string
  additional_fields?: Record<string, string>
  // Header auth fields
  headerKey?: string
  headerValue?: string
  token?: string
  custom_headers?: Record<string, string>
}

// API to export release configuration
export const exportReleaseConfig = async (
  data: Omit<ReleaseConfigData, 'release'>,
  koneSystem?: string,
  custId?: string,
) => {
  const payload: ReleaseConfigData = {
    ...data,
    release: true,
  }

  try {
    // Request the file data from the backend (now returns JSON with filename and hex-encoded file)
    const response = await apiClient.post('/cafm/get-integration-details', payload)

    console.log('Release config response:', response)

    // Check if response has the expected structure
    // The data might be at response.data level or response level depending on API client setup
    const responseData = response.data || response
    if (!responseData || !responseData.filename || !responseData.file) {
      throw new Error('Invalid response format: missing filename or file data')
    }

    const { filename, file: hexString } = responseData

    // Wrap with metadata so the UI can read kone_system on import.
    // Legacy exports (no koneSystem provided) store the raw hex string directly.
    const fileData = koneSystem
      ? JSON.stringify({ metadata: { kone_system: koneSystem, cust_id: custId }, file: hexString })
      : hexString

    // Create downloadable blob with the file data
    const blob = new Blob([fileData], { type: MIME_TYPES.APPLICATION_JSON })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename // Use exact filename from response
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 100)

    return response
  } catch (error) {
    console.error('Release config export failed:', error)
    throw error
  }
}

// API to import release configuration
export const importReleaseConfig = async (
  fileContent: string,
  filename: string,
  forceUpdate: boolean,
  orgId?: string,
) => {
  // Unwrap the metadata wrapper produced by the new export format.
  // Legacy exports store the raw hex string directly — those still work unchanged.
  let actualFileContent = fileContent
  try {
    const parsed = JSON.parse(fileContent)
    if (parsed?.file && parsed?.metadata) {
      actualFileContent = parsed.file
    }
  } catch {
    // Not a JSON wrapper — legacy format, use content as-is
  }

  try {
    const payload: Record<string, any> = {
      filename: filename || 'integration_export.json',
      file: actualFileContent,
      force_update: forceUpdate,
    }

    if (orgId) {
      payload.org_id = parseInt(orgId, 10)
    }

    const response = await apiClient.post('/cafm/import-integration', payload)
    return response
  } catch (error) {
    console.error('Release config import failed:', error)
    throw error
  }
}

// API to send integration configuration via email
interface SendIntegrationEmailRequest {
  integration_id: string
  customer_name: string
  base_url: string
  auth_type: 'basic' | 'custom' | 'header' | 'apiKey'
  user_email?: string // User email to be added to CC
  // Basic auth fields
  auth_url?: string
  username_body?: string
  password_body?: string
  // Custom auth fields
  username_auth?: string
  password_auth?: string
  content_type?: string
  token_delivery?: 'bearer' | 'x-api-key'
  response_key_path?: string
  expiry_key_path?: string
  token_prefix?: string
  additional_fields?: Record<string, string>
  // Header auth fields
  headerKey?: string
  headerValue?: string
  // API Key auth fields
  token?: string
}

export interface SendIntegrationEmailResponse {
  email_sent: boolean
  filename: string
  messageId: string
  recipients: string[]
}

export const sendIntegrationEmail = async (
  data: SendIntegrationEmailRequest,
): Promise<SendIntegrationEmailResponse> => {
  try {
    const response = await apiClient.post('/cafm/send-integration-email', data)
    return response.data
  } catch (error) {
    console.error('Send integration email failed:', error)
    throw error
  }
}

// API to sent data for execute tests for onboarding tool
export const runTests = async (runTestData: ExecuteTestData) => {
  try {
    const payload = {
      organization_id: runTestData.organization_id,
      integration_id: runTestData.integration_id,
      cust_id: runTestData.cust_id,
      integration_partener: runTestData.integration_partener,
      source_system: runTestData.source_system,
      ken_number: runTestData.ken,
      username: runTestData.username,
      password: runTestData.password,
      workorder_type: runTestData.workorder_type,
      test_cases: runTestData.test_cases,
      unique_id: runTestData.unique_id,
      testExecutionCooldownTime: runTestData.testExecutionCooldownTime,
    }

    const response = await apiClient.post('/cafm/execute-onboarding-tests', payload)
    return response
  } catch (error) {
    console.error('Run tests failed:', error)
    throw error
  }
}

// API to download onboarding tool test report
// uniqueKey format: <kone_system>-<integration_id> to get the uniqueId from DB
export const downloadTestReport = async (uniqueKey: string) => {
  try {
    const response = await apiClient.get(`/cafm/download-test-reports/${uniqueKey}`)
    return response
  } catch (error) {
    console.error('Download test report failed:', error)
    throw error
  }
}

// ─── File Upload APIs ────────────────────────────────────────────────────────

/**
 * Generate a pre-signed URL for uploading a Swagger or Config file to S3.
 * After receiving the URL, the client uploads the file directly to S3.
 */
export const getUploadPresignedUrl = async (payload: {
  integration_id?: string
  file_type: 'swagger' | 'config' | 'test_report'
  file_name: string
  content_type: string
  customer_name: string
  facility_name: string
  source_system?: string
  org_id?: number
  external_id?: string
  is_validation?: boolean
}) => {
  const response = await apiClient.post('/cafm/upload-file', payload)
  return response
}

/**
 * Upload a file to S3 using a pre-signed URL.
 * This calls S3 directly (not through the backend).
 */
export const uploadFileToS3 = async (uploadUrl: string, file: File, contentType: string) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`)
  }

  return response
}

/**
 * Get the S3 file paths for an integration (swagger and config files).
 */
export const getIntegrationFiles = async (integration_id: string) => {
  const response = await apiClient.post('/cafm/integration-files', { integration_id })
  return response
}

/**
 * Download the example config structure for a given source system.
 * Available as PDF, Excel (.xlsx) or Word (.docx) — all stored in S3 per source system
 * (Konect, FSM, Konect ENA, FSM CDM). Defaults to PDF when no format is given.
 */
export const downloadExampleConfig = async (sourceSystem: string, format: 'pdf' | 'excel' | 'word' = 'pdf') => {
  const response = await apiClient.post('/cafm/download-example-config', { source_system: sourceSystem, format })
  return response
}

/**
 * Download an uploaded integration file (Swagger or Config) from S3.
 * Returns a presigned download URL for the file.
 *
 * org_id / integration_id are sent so the backend orgCheckMiddleware can verify the
 * caller belongs to the organization that owns the file. Without an org-resolvable
 * field the middleware rejects the request with "User is not part of this organization".
 */
export const downloadIntegrationFile = async (
  s3Key: string,
  fileType: 'swagger' | 'config',
  orgId?: number | string,
  integrationId?: number | string,
) => {
  const response = await apiClient.post('/cafm/download-integration-file', {
    s3_key: s3Key,
    file_type: fileType,
    ...(orgId !== undefined && orgId !== null ? { org_id: orgId } : {}),
    ...(integrationId !== undefined && integrationId !== null ? { integration_id: integrationId } : {}),
  })
  return response
}

/**
 * Validates that a file is valid Swagger/OpenAPI JSON format.
 * Returns { valid: true } or { valid: false, error: string }
 */
export const validateSwaggerFile = (file: File): Promise<{ valid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target?.result as string)

        // Check for OpenAPI/Swagger identifiers
        if (!content.openapi && !content.swagger && !content.info) {
          resolve({
            valid: false,
            error:
              'File does not appear to be a valid Swagger/OpenAPI specification. Missing "openapi", "swagger", or "info" fields.',
          })
          return
        }

        // Check for paths or endpoints
        if (!content.paths && !content.definitions && !content.components) {
          resolve({ valid: false, error: 'File does not contain any API paths or definitions.' })
          return
        }

        resolve({ valid: true })
      } catch {
        resolve({ valid: false, error: 'File is not valid JSON. Please upload a valid Swagger/OpenAPI JSON file.' })
      }
    }
    reader.onerror = () => {
      resolve({ valid: false, error: 'Failed to read the file.' })
    }
    reader.readAsText(file)
  })
}

/**
 * Validates that a config file is in the correct format (PDF, Excel or Word).
 * Only the file type is validated here; the agent extracts and interprets the content.
 */
export const validateConfigFile = (file: File): Promise<{ valid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]

    // Some browsers report an empty file.type; fall back to the extension in that case.
    // Legacy .doc is excluded — the agent can only extract text from .docx.
    const ext = file.name.split('.').pop()?.toLowerCase()
    const extAllowed = ['pdf', 'xls', 'xlsx', 'docx'].includes(ext || '')

    if (!allowedTypes.includes(file.type) && !extAllowed) {
      resolve({ valid: false, error: 'Config file must be a PDF, Excel (.xls/.xlsx) or Word (.docx) file.' })
      return
    }

    // Type-only validation — the agent parses and interprets the actual content.
    resolve({ valid: true })
  })
}
